import StorageAPI from '@solinkcorp/settings/StorageAPI';
import { CenteredLoadingSpinner } from '@solinkcorp/solink-components';
import { auth } from '@solinkcorp/vd-common';
import AccessRestricted from 'components/AccessRestricted/AccessRestricted';
import Loadable from 'components/Loadable';
import { trackImpersonationReason } from 'core/analytics';
import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import {
  Navigate,
  type NavigateFunction,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import * as auth0 from './auth0';
import { type AuthCallbackResult } from './auth0';
import {
  getImpersonationURL,
  getLogoutURL,
  isCorrectTenantId,
  isLoginURL,
  locationToRelativePath,
} from './authUtils';
import AuthCallback from './components/AuthCallback';
import Logout from './components/Logout';
import SessionOutOfDateDialog from './components/SessionOutOfDateDialog';
import { AuthenticationContextProvider } from './internal/AuthenticationContext';
import { useLoginAnalytics } from './internal/useLoginAnalytics';
import { useRefreshUser } from './internal/useRefreshUser';
import { isBlockingOutage, shouldForceMajorOutage } from './outageStatus';
import SSOError from './SSOErrorPage';
import * as authActions from './state/authenticationActions';
import {
  type AuthenticationState,
  authenticationStateReducer,
  INITIAL_AUTHENTICATION_STATE,
} from './state/authenticationReducer';
import { getCoreTranslations } from './translations';
import { type LogoutHandler } from './types';
import { useOutageStatus } from './useOutageStatus';
import { userHasFeature } from './userFeatures';

/**
 * Local storage key used to temporarily save the original URL the user was
 * trying to visit while we go off to auth0.  This is always a relative URL.
 */
const ORIGINAL_URL_KEY = 'ORIGINAL_URL';

const ImpersonationPage = Loadable({
  loader: () => import('components/Impersonation/ImpersonationPage'),
});

const MajorOutagePage = Loadable({
  loader: () => import('./components/MajorOutagePage'),
});

/**
 * AuthenticationProvider handles authenticating users.
 *
 * The basic idea is that you wrap the application (or the part of the application
 * that needs authentication) in an `<AuthenticationProvider>` tag.  If a user
 * tries to render the page, and they are not authenticated, AuthenticationProvider
 * will not render any children, but will instead walk the user through the login
 * process.
 *
 * AuthenticationProvider also handles the "/login", "/logout", and
 * "/login/impersonation" routes.
 *
 * Any descendant component can use `useLoggedInUser()` to get the currently
 * logged in user.
 *
 * Note that AuthenticationProvider is guaranteed to be part of the "main bundle",
 * so nothing in this package should include any libraries we don't want in the
 * main bundle (e.g. redux, lodash, moment, solink-components, anything big).
 *
 * See the README.md in this folder for more details.
 */

interface AuthenticationProviderProps {
  children: React.ReactNode;
}

const AuthenticationProvider: React.FC<AuthenticationProviderProps> = ({ children }) => {
  const outageStatus = useOutageStatus();
  const location = useLocation();
  const navigate = useNavigate();

  const [authState, authDispatch] = useReducer(
    authenticationStateReducer,
    INITIAL_AUTHENTICATION_STATE,
  );

  // Create a single instance of logoutHandlers that never changes.
  const logoutHandlers = useRef<{ current: LogoutHandler }[]>([]);

  const i18n = getCoreTranslations(authState.userSession);

  useLoginAnalytics(authState.userSession, authState.impersonator, logoutHandlers.current);

  // Figure out who is already logged in before we do anything else...
  useEffect(() => loadInitialState(authDispatch), []);

  // If the user's session has expired, refresh it.
  const { showBadSessionDialog } = useRefreshUser(authDispatch, authState, logoutHandlers.current);

  // Called when we get a user from the Auth0 callback.
  const onLogin = useCallback(
    async (authResult: AuthCallbackResult): Promise<void> => {
      authDispatch(await authActions.loginAction(authResult));

      // After logging redirect to the original URL.
      redirectToOriginalURL(authResult, navigate);
    },
    [navigate],
  );

  // Called when someone selects a user to impersonate from the impersonation screen.
  // or onImpersonate is automatically called if userId is in search params
  const onImpersonate = useCallback(
    async (userId: string, redirectPath: string | undefined, reason?: string) => {
      if (authState.impersonator) {
        /**
         * non-persistant stuff stored in local should be cleared when switching impersonated user
         * For example in the case of STORED_REDUX_STORES the filtered location_id is no longer applicable
         * if the new impersonated user is associated with a different customer
         */
        StorageAPI.clearNonPersistent();
      }
      return impersonateNewUser(authState, authDispatch, navigate, userId, redirectPath, reason);
    },
    [authState, navigate],
  );

  if (authState.loading) {
    return <CenteredLoadingSpinner style={{ position: 'fixed' }} message={i18n.loading} />;
  }

  if (shouldForceMajorOutage() || isBlockingOutage(outageStatus?.status?.indicator)) {
    return (
      <AuthenticationContextProvider
        authState={authState}
        authDispatch={authDispatch}
        logoutHandlers={logoutHandlers.current}
      >
        <MajorOutagePage />
      </AuthenticationContextProvider>
    );
  }

  // Figure out what to render if we don't match any of the login routes.
  let defaultChildren: React.ReactNode;
  if (authState.userSession) {
    defaultChildren = children;
  } else if (authState.impersonator) {
    // Have an impersonator but no impersonated user...
    const currentURL = locationToRelativePath(location);
    defaultChildren = (
      <Navigate
        to={
          userHasFeature(
            {
              type: 'impersonator',
              isSolinkUser: auth.isSolinkEmail(authState.impersonator.profile.email),
              profile: authState.impersonator.profile,
            },
            'impersonateUsers',
          )
            ? getImpersonationURL(currentURL)
            : '/impersonation-restricted'
        }
        replace
      />
    );
  } else {
    // No one is logged in
    defaultChildren = <UserLogin />;
  }

  let impersonationPage = <UserLogin />;

  if (authState.impersonator) {
    if (
      userHasFeature(
        {
          type: 'impersonator',
          isSolinkUser: auth.isSolinkEmail(authState.impersonator.profile.email),
          profile: authState.impersonator.profile,
        },
        'impersonateUsers',
      )
    ) {
      impersonationPage = (
        <ImpersonationPage onImpersonate={onImpersonate} impersonator={authState.impersonator} />
      );
    } else {
      impersonationPage = <Navigate to="/impersonation-restricted" replace />;
    }
  }

  return (
    <AuthenticationContextProvider
      authState={authState}
      authDispatch={authDispatch}
      logoutHandlers={logoutHandlers.current}
    >
      <Routes>
        <Route path="/login" element={<UserLogin />} />
        <Route path="/sso-error" element={<SSOError />} />

        <Route
          path="/logout"
          element={
            <Logout userSession={authState.userSession} logoutHandlers={logoutHandlers.current} />
          }
        />
        <Route
          path="/login/callback"
          element={<AuthCallback onLogin={onLogin} logoutHandlers={logoutHandlers.current} />}
        />
        <Route path="/login/impersonation/:userId?" element={impersonationPage} />
        <Route
          path="/impersonation-restricted"
          element={
            <AccessRestricted
              message="You do not have the necessary permissions to impersonate accounts."
              redirectButtonText="Got it"
            />
          }
        />
        <Route path="/*" element={defaultChildren} />
      </Routes>
      {showBadSessionDialog && (
        <SessionOutOfDateDialog isImpersonating={!!authState.impersonator} />
      )}
    </AuthenticationContextProvider>
  );
};

export default React.memo(AuthenticationProvider);

/** Component for the /login path.  This redirects to the auth0 login page. */
const UserLogin: React.FC<unknown> = () => {
  useEffect(() => auth0.login(), []);
  return null;
};

/**
 * Load the initial state whenever the page reloads.
 */
function loadInitialState(authDispatch: React.Dispatch<authActions.AuthAction>) {
  storeOriginalURL();

  authActions
    .loadInitialState()
    .then(authDispatch, (err) => console.error('Unable to load initial state', err));
}

/**
 * Preserve the URL the user originally visited, so that if the user isn't logged
 * in, then after we go through auth0 we can redirect the user to the page they
 * were originally trying to visit.
 */
function storeOriginalURL() {
  const location = window.location;
  const currentURL = locationToRelativePath(location);

  if (!isLoginURL(currentURL)) {
    localStorage.setItem(ORIGINAL_URL_KEY, currentURL);
  }
}

function redirectToOriginalURL(authResult: AuthCallbackResult, navigate: NavigateFunction) {
  // A callback that bounced through a top-level re-authorize (see auth0.ts's
  // handleAuthCallback/getAccessTokenForAudience) carries its own returnTo;
  // otherwise fall back to the original URL saved before we went to auth0.
  const originalRelativeURL = authResult.returnTo || localStorage.getItem(ORIGINAL_URL_KEY) || '/';
  localStorage.removeItem(ORIGINAL_URL_KEY);

  if (authResult.snowflakeRecovery) {
    navigate(originalRelativeURL);
    return;
  }

  if (auth0.isImpersonator(authResult.profile)) {
    if (
      !userHasFeature(
        {
          isSolinkUser: auth.isSolinkEmail(authResult.profile.email),
          type: 'impersonator',
          profile: authResult.profile,
        },
        'impersonateUsers',
      )
    ) {
      navigate('/impersonation-restricted');
    } else {
      navigate(getImpersonationURL(originalRelativeURL));
    }
  } else {
    if (!isCorrectTenantId(originalRelativeURL, authResult.profile.customerId)) {
      navigate(getLogoutURL('WRONG_CUSTOMER', `${window.location.origin}${originalRelativeURL}`));
    } else {
      navigate(originalRelativeURL);
    }
  }
}

/**
 * Impersonate a user.
 */
async function impersonateNewUser(
  state: AuthenticationState,
  authDispatch: React.Dispatch<authActions.AuthAction>,
  navigate: NavigateFunction,
  userIdToImpersonate: string,
  redirectPath: string | undefined,
  reason: string | undefined,
): Promise<void> {
  if (!state.impersonator) {
    throw new Error(`Can't impersonate without an impersonator session!`);
  }

  // Redirect to the place we were asked to, or to the saved one in local storage if there is one.
  let path = redirectPath || '/';
  if (isLoginURL(path)) {
    path = '/';
  }

  // Impersonate the user.
  const impersonationResponse = await authActions.impersonateAction(
    state.impersonator.accessToken,
    userIdToImpersonate,
    reason,
  );
  authDispatch(impersonationResponse);

  if (reason) {
    await trackImpersonationReason(impersonationResponse.userSession, state.impersonator, reason);
  }

  if (state.userSession) {
    // Already impersonating another user.  Reload the whole page.
    if (!path.startsWith('https')) {
      path = `${window.location.origin}${path}`;
    }
    window.location.replace(path);
  } else {
    // Navigate to whatever page the user was trying to get to.
    navigate(path);
  }
}
